// Client for the Report Assignment API (lsdb_uat_api/report_assignment).
//
// The base URL comes from REACT_APP_REPORT_ASSIGNMENT_API_URL (set it to the
// local backend, e.g. http://127.0.0.1:8000/api/1.0/report-assignment/v1/, in
// .env.local). Without it the endpoints are resolved against the app's
// regular API host. Only this page uses this client.
import axios, { AxiosInstance } from "axios";
import { getBaseUrl } from "../../../../../common/util";
import {
  ChangeLogEntry,
  ConfirmAllResult,
  ProjectReports,
  ReadinessData,
  ReportMutationResult,
  WorkspaceData,
} from "./types";

export const getReportAssignmentBaseUrl = (): string => {
  const configured = process.env.REACT_APP_REPORT_ASSIGNMENT_API_URL;
  const base = configured || `${getBaseUrl()}report-assignment/v1/`;
  return base.endsWith("/") ? base : `${base}/`;
};

export const createReportAssignmentClient = (token: string | null): AxiosInstance => {
  const client = axios.create({ baseURL: getReportAssignmentBaseUrl() });
  client.interceptors.request.use((config) => {
    if (token) config.headers.Authorization = `Token ${token}`;
    return config;
  });
  return client;
};

export const reportAssignmentApi = (client: AxiosInstance) => ({
  workspace: async (projectId: string | number): Promise<WorkspaceData> =>
    (await client.get(`projects/${projectId}/workspace/`)).data,

  projectReports: async (projectId: string | number): Promise<ProjectReports> =>
    (await client.get(`projects/${projectId}/reports/`)).data,

  setModules: async (
    assignmentId: number,
    unitIds: number[],
    reason?: string,
  ): Promise<ReportMutationResult> =>
    (await client.put(`assignments/${assignmentId}/modules/`, { unit_ids: unitIds, reason })).data,

  confirm: async (assignmentId: number): Promise<ReportMutationResult> =>
    (await client.post(`assignments/${assignmentId}/confirm/`)).data,

  confirmAll: async (
    projectId: string | number,
    workOrderId: number,
  ): Promise<ConfirmAllResult> =>
    (await client.post(`projects/${projectId}/boms/${workOrderId}/confirm-all/`)).data,

  readiness: async (assignmentId: number): Promise<ReadinessData> =>
    (await client.get(`assignments/${assignmentId}/readiness/`)).data,

  history: async (projectId: string | number): Promise<ChangeLogEntry[]> =>
    (await client.get(`projects/${projectId}/history/`)).data,

  undo: async (historyId: number): Promise<{ report: ReportMutationResult["report"] }> =>
    (await client.delete(`history/${historyId}/`)).data,

  finish: async (projectId: string | number): Promise<unknown> =>
    (await client.post(`projects/${projectId}/finish/`)).data,
});

export type ReportAssignmentApi = ReturnType<typeof reportAssignmentApi>;
